import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { Button } from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core/helpers';
import { GuestsPicker } from '@nocowanie/common-ui/components/molecules/guests-picker';
import {
    RangePicker,
    RangePickerDates,
} from '@nocowanie/common-ui/components/molecules/range-picker';
import { GuestsPickerInputDataModel } from '@nocowanie/common-ui/models';
import { IconsLinear } from '@nocowanie/icons/linear';

import { environment } from '@app/environments/environment';
import * as accommodationHelpers from '@app/helpers/accommodation.helpers';
import { QueryAccommodationArgs, ReasonCode } from '@app/models/api/api-types';
import * as accommodationSlice from '@app/state/slices/accommodation.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { UnavailabilityCTAProps } from './unavailability-cta.props';
import { UnavailabilityCTAItem } from './unavailability-cta-item';

export const UnavailabilityCTA = ({
    buttonProps,
    textProps,
    hideLabels = false,
    isSingleApartment = false,
    labelsShort = false,
    saveToSession = false,
    ...props
}: UnavailabilityCTAProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'pricing',
    });
    const dispatch = useAppDispatch();
    const unavailabilityReason = useAppSelector(
        accommodationSlice.selectors.selectUnavailabilityReason,
    );
    const isUnavailableTooManyGuests = [ReasonCode.RequestNotSatisfied].includes(
        unavailabilityReason as ReasonCode,
    );
    const isDatesAvailabilityError = useAppSelector(
        accommodationSlice.selectors.selectIsDatesAvailabilityError,
    );
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const filters = useAppSelector(accommodationSlice.selectors.selectFilters);
    const { checkIn, checkOut } = filters ?? {};
    const isDatesNotSet = useMemo(() => isNil(checkIn) && isNil(checkOut), [checkIn, checkOut]);
    const selectedDates = accommodationHelpers.getDates(
        checkIn ?? new Date(),
        checkOut ?? new Date(),
    );
    const rangePickerDayConfig = useAppSelector(
        accommodationSlice.selectors.selectRangePickerDayConfig,
    );
    const rangePickerExcludedDays = useAppSelector(
        accommodationSlice.selectors.selectRangePickerExcludedDays,
    );
    const rangePickerErrorText = useAppSelector(
        accommodationSlice.selectors.selectCalendarAvailabilityErrorText,
    );

    const getLabelTranslationKey = (): string => {
        let label = 'error.unavailable';

        if (isDatesNotSet) {
            label = 'error.unavailableNoDatesSet';
        } else if (isUnavailableTooManyGuests) {
            label = 'error.unavailableGuestCountExceeded';
        }

        return label;
    };

    const getRangePickerLabel = (): string => {
        let label = labelsShort
            ? 'errorButtonLabels.changeDates'
            : 'errorButtonLabels.changeDatesLong';

        if (isDatesNotSet) {
            if (labelsShort) {
                label = 'errorButtonLabels.chooseDates';
            } else {
                label = 'errorButtonLabels.chooseDatesLong';
            }
        }

        return t(label);
    };

    const getRoomLabelTranslationKey = (): string => {
        let label = 'error.unavailableRoom';

        if (isDatesNotSet) {
            label = 'error.unavailableNoDatesSet';
        } else if (isUnavailableTooManyGuests) {
            label = 'error.unavailableRoomGuestCountExceeded';
        }

        return label;
    };

    return (
        <UnavailabilityCTAItem
            isSingleApartment={isSingleApartment}
            hideLabels={hideLabels}
            labelsShort={labelsShort}
            textProps={textProps}
            labelTranslationKey={getLabelTranslationKey()}
            roomLabelTranslationKey={getRoomLabelTranslationKey()}
            customErrorText={
                !isUnavailableTooManyGuests && rangePickerErrorText
                    ? rangePickerErrorText
                    : undefined
            }
            {...props}
        >
            {isUnavailableTooManyGuests ? (
                <GuestsPicker
                    inputData={
                        {
                            adultsCount: filters?.guestsCount,
                            childrenCount: filters?.childrenCount,
                            childrenAges: filters?.childrenAges,
                        } as GuestsPickerInputDataModel
                    }
                    noArrow={true}
                    onInputDataChanged={data => {
                        dispatch(
                            accommodationSlice.actions.updateFilters({
                                guestsCount: data.adultsCount,
                                childrenCount: data.childrenCount,
                                childrenAges:
                                    data.childrenAges as QueryAccommodationArgs['childrenAges'],
                            }),
                        );
                    }}
                    isMobileBrowser={isMobileBrowser}
                    saveToSession={saveToSession}
                >
                    <Button variant={'outline'} {...buttonProps}>
                        {!labelsShort ? <IconsLinear.User mr={2} /> : null}
                        {t(
                            labelsShort
                                ? 'errorButtonLabels.changeGuestCount'
                                : 'errorButtonLabels.changeGuestCountLong',
                        )}
                    </Button>
                </GuestsPicker>
            ) : (
                <RangePicker
                    isClearable
                    selectedDates={selectedDates}
                    onRangeChange={(dates: RangePickerDates) => {
                        dispatch(
                            accommodationSlice.actions.updateFilters({
                                checkIn: dates[0]
                                    ? dateHelpers.format(dates[0], environment.API_DATE_FORMAT)
                                    : null,
                                checkOut: dates[1]
                                    ? dateHelpers.format(dates[1], environment.API_DATE_FORMAT)
                                    : null,
                            }),
                        );
                    }}
                    isMobileBrowser={isMobileBrowser}
                    pickerProps={{
                        popperClassName: !isMobileBrowser
                            ? 'react-datepicker-popper--full'
                            : undefined,
                    }}
                    daysConfig={rangePickerDayConfig}
                    excludeDateStrings={rangePickerExcludedDays}
                    inputProps={{
                        invalid: isDatesAvailabilityError,
                    }}
                    saveToSession={saveToSession}
                >
                    <Button variant={'outline'} {...buttonProps}>
                        {!labelsShort ? <IconsLinear.Calendar mr={2} /> : null}
                        {getRangePickerLabel()}
                    </Button>
                </RangePicker>
            )}
        </UnavailabilityCTAItem>
    );
};
